/**
 * "The server never got to say whether these credentials are good."
 *
 * A 401 that survives the interceptor usually means the session is over. But
 * the same 401 comes back when the refresh that would have settled the question
 * never reached a verdict — a 5xx or a dropped connection on the way to
 * `/auth/refresh` — and that says nothing about the tokens, which are still in
 * storage and may still be perfectly good. Marking the difference is what lets
 * the UI show "we can't tell" instead of a sign-in form.
 *
 * A symbol rather than a property: the error object belongs to axios, so this
 * must not collide with its own fields, show up in `JSON.stringify` or
 * `Object.keys`, or reach a logger.
 *
 * `Symbol.for` rather than `Symbol()` because the interceptor's tests reload
 * the module under test with `vi.resetModules()`. A fresh `Symbol()` would be a
 * different key from the one the test file's own import holds, and every
 * assertion would quietly read false.
 */
const unadjudicatedKey = Symbol.for('tixly.auth.unadjudicated');

export const markUnadjudicated = <T>(error: T): T => {
  if (error && typeof error === 'object') {
    (error as Record<symbol, unknown>)[unadjudicatedKey] = true;
  }
  return error;
};

export const isUnadjudicated = (error: unknown): boolean =>
  !!error &&
  typeof error === 'object' &&
  (error as Record<symbol, unknown>)[unadjudicatedKey] === true;
